import { Component, OnInit, ViewChild, effect, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { AiOrchestratorResult, ChatComponent, SplitPaneComponent } from '@components';
import { SplitPaneVerticalComponent, HistoryComponent } from '@components';
import { SkeletonBlockComponent } from '@components';
import { DefineComponent, DefineModel, DefineService, DefineDefinition, DefineFacade, WorkspaceFacade, hydrateDefinition, initialDefineDefinition } from '@state';
import { ToastService } from '@services';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { applyJsonPatchOps, JsonPatchOp } from '../../utils/json-patch';
import { getDefinitionReadiness } from '../../utils/definition-readiness';
import { buildDefinitionCoverageChecklistItems } from '../../utils/definition-checklist.util';
import { compactDefinitionForOrchestrator } from '../../utils/orchestrator-definition-context.util';

@Component({
  selector: 'app-define',
  templateUrl: './define.page.html',
  styleUrls: ['./define.page.scss'],
  standalone: true,
  imports: [
    RouterModule,
    NgbTooltip,
    ChatComponent,
    HistoryComponent,
    SplitPaneComponent,
    SplitPaneVerticalComponent,
    SkeletonBlockComponent,
    FormsModule,
],
})
export class DefinePage implements OnInit {
  // Use template ref `#defineChat` in the template.
  @ViewChild('defineChat') public defineChat?: ChatComponent;

  public defineSuggestions: any[] = [
    {
      icon: 'bi bi-magic',
      prompt: 'Scaffold and host a new project with Angular',
    },
    {
      icon: 'bi bi-calculator',
      prompt: 'Create a quoting system for my business',
    },
    {
      icon: 'bi bi-code-square',
      prompt: 'Generate a simple landing page with HTML and CSS',
    },
    { icon: 'bi bi-globe', prompt: 'Recommend a framework' },
    { icon: 'bi bi-file-earmark-bar-graph', prompt: 'Create a report' },
    {
      icon: 'bi bi-ticket-perforated',
      prompt:
        'Define some critical tickets for my project, I need to cover all the bases',
    },
  ];

  public defineContext: any = {
    page: 'define',
    purpose: 'Assist in defining a new project with clear scope and deliverables',
    project: {
      name: '',
      description: '',
      domain: '',
      targetUsers: '',
      successCriteria: []
    },
    goals: [],
    features: [],
    constraints: [],
    stakeholders: [],
    assumptions: [],
    outputs: [
      'Project summary',
      'Key features list',
      'Data models/entities',
      'High-level architecture',
      'Initial ticket backlog'
    ]
  };

  public definitionDraft: DefineDefinition = structuredClone(initialDefineDefinition);

  // UI helpers for “add item” inputs
  public addInputs: Record<string, string> = {
    successCriteria: '',
    goals: '',
    features: '',
    constraints: '',
    stakeholders: '',
    assumptions: '',
    personas: '',
    userJourneys: '',
    inScope: '',
    outOfScope: '',
    risks: '',
    milestones: '',
    integrations: '',
    keyScreens: '',
    modelField: '',
    serviceEndpoint: '',
  };

  public newModel: DefineModel = { name: '', description: '', fields: [] };
  public newService: DefineService = { name: '', description: '', endpoints: [] };
  public newComponent: DefineComponent = { name: '', description: '' };

  public defineStore = inject(DefineFacade);
  private rootStore = inject(WorkspaceFacade);
  private toast = inject(ToastService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  public saving = false;
  public dirty = false;
  public showDetailedView = false;
  public definitionValidationError = '';
  private baselineSnapshot = '';
  private lastLoadedProjectId: string | null = null;
  private loadInFlight: Promise<void> | null = null;
  public debugEnabled(): boolean {
    try {
      return typeof localStorage !== 'undefined' && localStorage.getItem('debugDefine') === '1';
    } catch {
      return false;
    }
  }

  get debugInfo() {
    const pid = this.defineStore.activeProjectId();
    const def = this.defineStore.definition();
    return {
      storeInitialized: this.rootStore.initialized(),
      activeProjectId: pid,
      loadedName: def?.name ?? '',
      loading: this.defineStore.loading?.() ?? null,
      error: this.defineStore.error?.() ?? null,
    };
  }

  constructor() {
    // Auto-load once the active project becomes available (important on hard refresh).
    effect(() => {
      const pid = this.defineStore.activeProjectId();
      if (!pid) return;
      if (pid === this.lastLoadedProjectId) return;
      this.lastLoadedProjectId = pid;
      void this.loadDefinition();
    });

    effect(() => {
      const def = this.defineStore.definition();
      this.definitionDraft = hydrateDefinition(def);

      // Reset dirty baseline after hydrating draft from store.
      this.resetBaseline();
    });
  }

  public onGenerateFromRepo() {
    const prompt = [
      'Generate a complete project definition from all connected GitHub repos.',
      '',
      'Requirements:',
      '- Treat pageContext.repoContext.github.repos as source of truth (not just existing definition).',
      '- You MUST inspect every connected repo in pageContext.repoContext.github.repos.',
      '- For EACH repo: call github_get_tree first, then read high-signal files with github_get_content (README, docs, manifests, migrations, OpenAPI, config, CI).',
      '- Populate ALL Define fields.',
      '- Include evidence grouped per repo/role in your message (site/api/agent-api or owner/repo groups).',
      '- If fewer than 2 repos are inspected while multiple repos are connected, return patches:[] and ask to retry.',
    ].join('\n');
    this.defineChat?.sendInjectedPrompt(prompt);
  }

  get hasActiveDefineChat(): boolean {
    return (this.defineStore.chatMessages()?.length ?? 0) > 0;
  }

  get canShowGenerateFromRepoButton(): boolean {
    const github = this.defineStore.repoContext()?.github;
    const hasInstallation = (github?.installationId ?? null) !== null;
    const hasRepos = (github?.repos?.length ?? 0) > 0;
    return hasInstallation && hasRepos && !this.hasActiveDefineChat;
  }

  get defineOrchestratorContext(): any {
    const repoContext = this.defineStore.repoContext()?.github;
    return {
      definition: compactDefinitionForOrchestrator(this.definitionDraft),
      repoContext: repoContext
        ? {
            github: {
              installationId: repoContext.installationId ?? null,
              repos: Array.isArray(repoContext.repos) ? repoContext.repos.slice(0, 10) : [],
            },
          }
        : null,
    };
  }

  async ngOnInit(): Promise<void> {
    await this.loadDefinition();

    // If Home triggered a resync, kick off a from-repo generation automatically.
    try {
      const resync = String(this.route.snapshot.queryParamMap.get('resync') ?? '').trim();
      if (resync === '1') {
        // Strip param so refresh doesn't re-run.
        void this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { resync: null },
          queryParamsHandling: 'merge',
          replaceUrl: true,
        });

        // Wait for chat to exist (view init) then inject prompt.
        const start = Date.now();
        const tick = () => {
          if (this.defineChat) {
            this.onGenerateFromRepo();
            return;
          }
          if (Date.now() - start > 4_000) return;
          setTimeout(tick, 50);
        };
        setTimeout(tick, 50);
      }
    } catch {
      // ignore
    }
  }

  private async loadDefinition(): Promise<void> {
    if (this.loadInFlight) return this.loadInFlight;
    this.loadInFlight = (async () => {
      // Wait briefly for project selection to hydrate (on hard refresh).
      for (let i = 0; i < 12; i++) {
        const pid = this.defineStore.activeProjectId();
        if (this.debugEnabled()) console.log('[DefinePage.loadDefinition] wait', { i, pid });
        if (pid) break;
        await new Promise((r) => setTimeout(r, 150));
      }
      if (this.debugEnabled()) {
        console.log('[DefinePage.loadDefinition] calling DefineStore.load', {
          activeProjectId: this.defineStore.activeProjectId(),
        });
      }
      await this.defineStore.load();
      if (this.debugEnabled()) {
        const def = this.defineStore.definition();
        console.log('[DefinePage.loadDefinition] after load', {
          activeProjectId: this.defineStore.activeProjectId(),
          storeName: def?.name ?? null,
          error: this.defineStore.error?.() ?? null,
        });
      }
    })().finally(() => {
      this.loadInFlight = null;
    });
    return this.loadInFlight;
  }

  async onChatMessagesChange(messages: Array<{ role: 'user' | 'assistant'; text: string }>) {
    await this.defineStore.setChatMessages(messages);
  }

  async onChatMessageCreated(msg: { role: 'user' | 'assistant'; text: string }) {
    await this.defineStore.logChatEvent(msg.text, msg.role);
  }

  async onAiResult(result: AiOrchestratorResult) {
    if (!result || String(result.page ?? '').toLowerCase() !== 'define') return;
    const opsRaw = (Array.isArray(result.patches) ? result.patches : []) as JsonPatchOp[];
    const ops = opsRaw.map((op) => {
      const path = String((op as any)?.path ?? '');
      // The agent commonly patches relative to the wrapper we send in pageContext:
      //   { definition: <DefineDefinition> }
      // Normalize those to patch the definition object directly.
      if (path === '/definition') {
        return { ...(op as any), path: '' } as JsonPatchOp;
      }
      if (path.startsWith('/definition/')) {
        return { ...(op as any), path: path.slice('/definition'.length) } as JsonPatchOp;
      }
      return op;
    });
    if (ops.length === 0) return;

    try {
      const next = applyJsonPatchOps<DefineDefinition>(this.definitionDraft, ops);

      if (!next) return;

      // Guard: never persist an accidental nested `definition` field.
      if ((next as any).definition !== undefined) {
        delete (next as any).definition;
      }
      if ((this.definitionDraft as any).definition !== undefined) {
        delete (this.definitionDraft as any).definition;
      }

      // Update the draft immediately for UI responsiveness.
      this.definitionDraft = next;

      // Auto-save (per chosen behavior).
      this.saving = true;
      this.defineStore.saveDefinition(next);
      this.resetBaseline();
      this.toast.success('AI updated project definition', 'Define');
    } catch (e: any) {
      this.toast.error(e?.message ?? 'Failed to apply AI updates', 'Define');
    } finally {
      this.saving = false;
    }
  }

  private buildSavePatch(): Partial<DefineDefinition> {
    const toCleanString = (x: any): string => {
      if (x === null || x === undefined) return '';
      if (typeof x === 'string') return x.trim();
      if (typeof x === 'number' || typeof x === 'boolean') return String(x).trim();
      if (typeof x === 'object') {
        // Best-effort extraction for common model outputs.
        const candidate =
          (x as any).text ??
          (x as any).name ??
          (x as any).title ??
          (x as any).field ??
          (x as any).endpoint ??
          (x as any).value ??
          (x as any).description ??
          null;
        if (candidate !== null && candidate !== undefined) return toCleanString(candidate);
        try {
          return JSON.stringify(x).trim();
        } catch {
          return String(x).trim();
        }
      }
      return String(x).trim();
    };

    const cleanList = (xs: any[]) => (xs ?? []).map(toCleanString).filter((s) => Boolean(s && s !== '[object Object]'));
    const pruneEmpty = (v: any): any => {
      if (v === null || v === undefined) return undefined;
      if (typeof v === 'string') return v.trim() ? v.trim() : undefined;
      if (Array.isArray(v)) return v.length ? v : undefined;
      if (typeof v !== 'object') return v;
      const out: any = {};
      for (const k of Object.keys(v)) {
        const pv = pruneEmpty(v[k]);
        if (pv !== undefined) out[k] = pv;
      }
      return Object.keys(out).length ? out : undefined;
    };

    // Preserve structured model fields / service endpoints (objects) instead of collapsing to strings.
    const cleanModelField = (f: any) => {
      if (f === null || f === undefined) return null;
      if (typeof f === 'string') {
        const name = toCleanString(f);
        if (!name) return null;
        return { name, type: '' };
      }
      if (typeof f !== 'object') {
        const name = toCleanString(f);
        if (!name) return null;
        return { name, type: '' };
      }
      const name = toCleanString((f as any).name ?? (f as any).field ?? (f as any).columnName ?? (f as any).title ?? '');
      if (!name) return null;
      const type = toCleanString((f as any).type ?? (f as any).dataType ?? (f as any).fieldType ?? '');
      const cleaned = pruneEmpty({
        name,
        type,
        pk: (f as any).pk === undefined ? undefined : !!(f as any).pk,
        fk: (f as any).fk === undefined ? undefined : !!(f as any).fk,
        nullable: (f as any).nullable === undefined ? undefined : !!(f as any).nullable,
        unique: (f as any).unique === undefined ? undefined : !!(f as any).unique,
        default: (f as any).default === undefined ? undefined : toCleanString((f as any).default),
      });
      return cleaned ?? { name, type };
    };
    const cleanModelFields = (xs: any[]) => (xs ?? []).map(cleanModelField).filter(Boolean);

    const cleanServiceEndpoint = (e: any) => {
      if (e === null || e === undefined) return null;
      if (typeof e === 'string') {
        const v = toCleanString(e);
        return v ? v : null;
      }
      if (typeof e !== 'object') {
        const v = toCleanString(e);
        return v ? v : null;
      }
      const name = toCleanString((e as any).name ?? (e as any).endpoint ?? (e as any).path ?? (e as any).title ?? '');
      if (!name) return null;
      const description = toCleanString((e as any).description ?? '');
      return pruneEmpty({ name, description }) ?? { name };
    };
    const cleanServiceEndpoints = (xs: any[]) => (xs ?? []).map(cleanServiceEndpoint).filter(Boolean);

    const cleanModels = (xs: DefineModel[]) =>
      (xs ?? [])
        .map((m) => ({
          name: toCleanString((m as any)?.name),
          description: toCleanString((m as any)?.description),
          fields: cleanModelFields((m as any)?.fields ?? []),
        }))
        .filter((m) => m.name.length > 0);
    const cleanServices = (xs: DefineService[]) =>
      (xs ?? [])
        .map((s) => ({
          name: toCleanString((s as any)?.name),
          description: toCleanString((s as any)?.description),
          endpoints: cleanServiceEndpoints((s as any)?.endpoints ?? []),
        }))
        .filter((s) => s.name.length > 0);
    const cleanComponents = (xs: DefineComponent[]) =>
      (xs ?? [])
        .map((c) => ({
          name: toCleanString((c as any)?.name),
          description: toCleanString((c as any)?.description),
        }))
        .filter((c) => c.name.length > 0);

    return (pruneEmpty({
      name: toCleanString((this.definitionDraft as any).name),
      description: toCleanString((this.definitionDraft as any).description),
      domain: toCleanString((this.definitionDraft as any).domain),
      targetUsers: toCleanString((this.definitionDraft as any).targetUsers),
      budget: toCleanString((this.definitionDraft as any).budget),
      teamSize: toCleanString((this.definitionDraft as any).teamSize),
      timeline: toCleanString((this.definitionDraft as any).timeline),
      successCriteria: cleanList(this.definitionDraft.successCriteria),
      goals: cleanList(this.definitionDraft.goals),
      features: cleanList(this.definitionDraft.features),
      constraints: cleanList(this.definitionDraft.constraints),
      stakeholders: cleanList(this.definitionDraft.stakeholders),
      assumptions: cleanList(this.definitionDraft.assumptions),
      nonFunctionalRequirements: {
        performance: toCleanString((this.definitionDraft as any).nonFunctionalRequirements?.performance),
        scalability: toCleanString((this.definitionDraft as any).nonFunctionalRequirements?.scalability),
        availability: toCleanString((this.definitionDraft as any).nonFunctionalRequirements?.availability),
        security: toCleanString((this.definitionDraft as any).nonFunctionalRequirements?.security),
        privacy: toCleanString((this.definitionDraft as any).nonFunctionalRequirements?.privacy),
        compliance: toCleanString((this.definitionDraft as any).nonFunctionalRequirements?.compliance),
        accessibility: toCleanString((this.definitionDraft as any).nonFunctionalRequirements?.accessibility),
      },
      personas: cleanList(this.definitionDraft.personas),
      userJourneys: cleanList(this.definitionDraft.userJourneys),
      inScope: cleanList(this.definitionDraft.inScope),
      outOfScope: cleanList(this.definitionDraft.outOfScope),
      risks: cleanList(this.definitionDraft.risks),
      milestones: cleanList(this.definitionDraft.milestones),
      releasePlan: toCleanString((this.definitionDraft as any).releasePlan),
      integrations: cleanList(this.definitionDraft.integrations),
      environmentsAndDeployment: {
        hosting: toCleanString((this.definitionDraft as any).environmentsAndDeployment?.hosting),
        cicd: toCleanString((this.definitionDraft as any).environmentsAndDeployment?.cicd),
        environments: toCleanString((this.definitionDraft as any).environmentsAndDeployment?.environments),
        observability: toCleanString((this.definitionDraft as any).environmentsAndDeployment?.observability),
        backups: toCleanString((this.definitionDraft as any).environmentsAndDeployment?.backups),
      },
      permissionsAndRoles: toCleanString((this.definitionDraft as any).permissionsAndRoles),
      testingStrategy: toCleanString((this.definitionDraft as any).testingStrategy),
      uxNotes: toCleanString((this.definitionDraft as any).uxNotes),
      keyScreens: cleanList(this.definitionDraft.keyScreens),
      models: cleanModels(this.definitionDraft.models),
      services: cleanServices(this.definitionDraft.services),
      components: cleanComponents(this.definitionDraft.components),
      architectureNotes: toCleanString((this.definitionDraft as any).architectureNotes),
    }) ?? {}) as any;
  }

  private snapshot(): string {
    // Stable “dirty” snapshot based on what we would actually save.
    return JSON.stringify(this.buildSavePatch());
  }

  private refreshDirty(): void {
    this.dirty = this.snapshot() !== this.baselineSnapshot;
  }

  private resetBaseline(): void {
    this.baselineSnapshot = this.snapshot();
    this.dirty = false;
  }

  public hasUnsavedChanges(): boolean {
    return this.dirty || this.saving;
  }

  get definitionReadiness() {
    return getDefinitionReadiness(this.definitionDraft);
  }

  get canContinueToTickets(): boolean {
    return this.definitionReadiness.ready && !this.hasUnsavedChanges();
  }

  get continueChecklistItems(): Array<{ key: string; label: string; done: boolean; hint?: string }> {
    const readiness = this.definitionReadiness;
    const nameOk = String(this.definitionDraft?.name ?? '').trim().length > 0;
    const descOk = String(this.definitionDraft?.description ?? '').trim().length > 0;
    const goals = Array.isArray(this.definitionDraft?.goals) ? this.definitionDraft.goals.length : 0;
    const features = Array.isArray(this.definitionDraft?.features) ? this.definitionDraft.features.length : 0;
    const inScope = Array.isArray(this.definitionDraft?.inScope) ? this.definitionDraft.inScope.length : 0;
    const saved = !this.hasUnsavedChanges();

    return [
      {
        key: 'basic',
        label: 'Basic info (name + description)',
        done: nameOk && descOk,
        hint: !nameOk ? 'Add a project name.' : !descOk ? 'Add a description.' : undefined,
      },
      ...buildDefinitionCoverageChecklistItems({ goals, features, inScope }),
      {
        key: 'saved',
        label: 'Saved (no unsaved changes)',
        done: saved,
        hint: saved ? undefined : 'Click Save to continue.',
      },
      {
        key: 'ready',
        label: 'Ready for DB design',
        done: readiness.ready && saved,
        hint: readiness.ready && saved ? undefined : undefined,
      },
    ];
  }

  public onContinueToTickets(): void {
    if (!this.canContinueToTickets) return;
    void this.router.navigate(['/db-design']);
  }

  public onFormInput(): void {
    // Wait a microtask so [(ngModel)] has applied changes.
    queueMicrotask(() => this.refreshDirty());
    this.definitionValidationError = '';
  }

  public onEnterToSave(event: KeyboardEvent): void {
    if (!this.dirty || this.saving) return;
    const el = event.target as any;
    const tag = String(el?.tagName ?? '').toUpperCase();

    // Don't save when using list "Add ..." inputs (they usually expect Enter to add, not save).
    const placeholder = typeof el?.placeholder === 'string' ? el.placeholder.toLowerCase() : '';
    if (placeholder.startsWith('add ')) return;

    // Textareas should keep newline; allow Ctrl/Cmd+Enter to save.
    if (tag === 'TEXTAREA' && !(event.ctrlKey || event.metaKey)) return;

    // Only trigger on typical form controls.
    if (tag !== 'INPUT' && tag !== 'SELECT' && tag !== 'TEXTAREA') return;

    event.preventDefault();
    void this.saveDefinition();
  }

  async saveDefinition() {
    if (this.saving) return;
    this.definitionValidationError = '';
    if (!String(this.definitionDraft.name ?? '').trim()) {
      this.definitionValidationError = 'Project name is required.';
      this.toast.error(this.definitionValidationError, 'Define');
      return;
    }
    if (!String(this.definitionDraft.domain ?? '').trim()) {
      this.definitionValidationError = 'Product type/category is required.';
      this.toast.error(this.definitionValidationError, 'Define');
      return;
    }
    this.saving = true;
    try {
      // Build a PATCH payload so we don't wipe the whole definition when only a field changes.
      const patch = this.buildSavePatch();

      this.defineStore.saveDefinition(patch as DefineDefinition);
      this.resetBaseline();
      this.toast.success('Project definition saved', 'Define');
    } catch (e: any) {
      this.toast.error(e?.message ?? 'Failed to save project definition', 'Define');
    } finally {
      this.saving = false;
    }
  }

  addToList(
    key: keyof Pick<
      DefineDefinition,
      | 'successCriteria'
      | 'goals'
      | 'features'
      | 'constraints'
      | 'stakeholders'
      | 'assumptions'
      | 'personas'
      | 'userJourneys'
      | 'inScope'
      | 'outOfScope'
      | 'risks'
      | 'milestones'
      | 'integrations'
      | 'keyScreens'
    >
  ) {
    const value = (this.addInputs[key] || '').trim();
    if (!value) return;
    (this.definitionDraft[key] as string[]).push(value);
    this.addInputs[key] = '';
    this.refreshDirty();
  }

  removeFromList(
    key: keyof Pick<
      DefineDefinition,
      | 'successCriteria'
      | 'goals'
      | 'features'
      | 'constraints'
      | 'stakeholders'
      | 'assumptions'
      | 'personas'
      | 'userJourneys'
      | 'inScope'
      | 'outOfScope'
      | 'risks'
      | 'milestones'
      | 'integrations'
      | 'keyScreens'
    >,
    idx: number
  ) {
    (this.definitionDraft[key] as string[]).splice(idx, 1);
    this.refreshDirty();
  }

  addModelField() {
    const value = (this.addInputs['modelField'] || '').trim();
    if (!value) return;
    (this.newModel.fields ??= []).push(value);
    this.addInputs['modelField'] = '';
  }

  removeModelField(idx: number) {
    (this.newModel.fields ?? []).splice(idx, 1);
  }

  addModel() {
    const name = (this.newModel.name || '').trim();
    if (!name) return;
    this.definitionDraft.models.push({
      name,
      description: (this.newModel.description || '').trim(),
      fields: (this.newModel.fields ?? []).slice(),
    });
    this.newModel = { name: '', description: '', fields: [] };
    this.refreshDirty();
  }

  removeModel(idx: number) {
    this.definitionDraft.models.splice(idx, 1);
    this.refreshDirty();
  }

  addServiceEndpoint() {
    const value = (this.addInputs['serviceEndpoint'] || '').trim();
    if (!value) return;
    (this.newService.endpoints ??= []).push(value);
    this.addInputs['serviceEndpoint'] = '';
  }

  removeServiceEndpoint(idx: number) {
    (this.newService.endpoints ?? []).splice(idx, 1);
  }

  addService() {
    const name = (this.newService.name || '').trim();
    if (!name) return;
    this.definitionDraft.services.push({
      name,
      description: (this.newService.description || '').trim(),
      endpoints: (this.newService.endpoints ?? []).slice(),
    });
    this.newService = { name: '', description: '', endpoints: [] };
    this.refreshDirty();
  }

  removeService(idx: number) {
    this.definitionDraft.services.splice(idx, 1);
    this.refreshDirty();
  }

  // Display helpers: avoid `[object Object]` when arrays contain rich objects
  formatModelFields(fields: any): string {
    const xs = Array.isArray(fields) ? fields : [];
    const fmt = (f: any) => {
      if (f === null || f === undefined) return '';
      if (typeof f === 'string') return f.trim();
      if (typeof f !== 'object') return String(f).trim();
      const name = String(f?.name ?? '').trim();
      const type = String(f?.type ?? '').trim();
      const flags: string[] = [];
      if (f?.pk) flags.push('PK');
      if (f?.fk) flags.push('FK');
      if (f?.unique) flags.push('UNIQUE');
      const flagStr = flags.length ? ` (${flags.join(', ')})` : '';
      const typeStr = type ? `: ${type}` : '';
      return `${name}${flagStr}${typeStr}`.trim();
    };
    return xs.map(fmt).filter(Boolean).join(', ');
  }

  formatServiceEndpoints(endpoints: any): string {
    const xs = Array.isArray(endpoints) ? endpoints : [];
    const fmt = (e: any) => {
      if (e === null || e === undefined) return '';
      if (typeof e === 'string') return e.trim();
      if (typeof e !== 'object') return String(e).trim();
      return String(e?.name ?? '').trim();
    };
    return xs.map(fmt).filter(Boolean).join(', ');
  }

  addComponent() {
    const name = (this.newComponent.name || '').trim();
    if (!name) return;
    this.definitionDraft.components.push({
      name,
      description: (this.newComponent.description || '').trim(),
    });
    this.newComponent = { name: '', description: '' };
    this.refreshDirty();
  }

  removeComponent(idx: number) {
    this.definitionDraft.components.splice(idx, 1);
    this.refreshDirty();
  }

  get primaryGoalsText(): string {
    return (this.definitionDraft.goals ?? []).join('\n');
  }

  set primaryGoalsText(value: string) {
    this.definitionDraft.goals = String(value ?? '')
      .split('\n')
      .map((x) => x.trim())
      .filter(Boolean);
    this.refreshDirty();
  }
}
